import type { Workbook } from '@mathflat/domain/Workbook'

import type { StudentFeatureKey } from './FeaturePermission'
import type { UserPermission } from './UserPermission'

/** 학습지화면 학부모 식별용 write 키 */
export const WORKSHEET_WRITE_KEY: StudentFeatureKey = 'CUSTOM_WORKSHEET_WRITE'
/** 시험 채점 페이지의 학부모 식별 write 키. */
export const EXAM_WRITE_KEY: StudentFeatureKey = 'EXAM_WORKSHEET_WRITE'
/**
 * write 권한 차단 = 학부모 모드.
 *
 * 페이지/서비스에서 ProblemScoringViewOption 생성 직전에 호출,
 * 결과를 그대로 viewerMode 인자로 넘기면 됨.
 */

/**
 * `writeKey` 가 null 이면 write 차단 키 자체가 없는 케이스(예: 학원그룹교재 = 모두 지원).
 * 학부모 식별 불가 → 항상 'student' 모드로 fallback.
 */
export const resolveViewerMode = (
  permission: UserPermission,
  writeKey: StudentFeatureKey | null,
): 'student' | 'parent' => {
  if (writeKey === null) return 'student'
  return permission.byFeature.isAllowed(writeKey) ? 'student' : 'parent'
}

/**
 * 쓰기 차단·표시 정책용 학부모 판별. userRole 과 write restriction 중 하나라도 학부모면 참이다.
 * 두 신호가 어긋나는 토큰에서도 학생 답안이 새거나 학부모가 필기하는 일이 없도록 넓게 잡는다.
 *
 * 경로 선택(어디로 보낼지)에는 쓰지 말 것 — 그건 목적지를 심판하는 PermissionGuardRoute 와
 * 같은 신호(userRole)여야 만든 경로가 가드에 튕기지 않는다.
 */
export const isParentViewer = (permission: UserPermission): boolean =>
  permission.isParent || !permission.byFeature.isAllowed(WORKSHEET_WRITE_KEY)

/**
 * 학습지 채점 페이지의 풀이동영상 restriction 키.
 */
export const WORKSHEET_PROBLEM_SOLVED_VIDEO_KEY: StudentFeatureKey =
  'CUSTOM_WORKSHEET_PROBLEM_SOLVED_VIDEO'

/** 시험 채점 페이지의 풀이동영상 restriction 키. */
export const EXAM_PROBLEM_SOLVED_VIDEO_KEY: StudentFeatureKey =
  'EXAM_WORKSHEET_PROBLEM_SOLVED_VIDEO'
/**
 * 풀이동영상 차단 여부.
 * restriction 이 AppSettings 보다 우선 적용되어야 하므로,
 * 결과를 ProblemScoringViewOption 의 videoRestricted 인자로 그대로 전달.
 *
 * `videoKey` 가 null 이면 차단 키 자체가 없는 케이스(예: 학원그룹교재 = 모두 지원) → 항상 허용.
 */
export const isVideoRestricted = (
  permission: UserPermission,
  videoKey: StudentFeatureKey | null,
): boolean => videoKey != null && !permission.byFeature.isAllowed(videoKey)

/**
 * 학습시간 트래킹 restriction 키 (POST /student-learning/time*).
 * 차단되면 학습지/시험/교재/인강/개념학습 등 어떤 페이지에서도 학습시간 API 미호출.
 */
export const LEARNING_TIMER_KEY: StudentFeatureKey = 'LEARNING_TIMER'

/**
 * 교재(WORKBOOK) 의 type 별 학부모 식별용 write 키.
 * - 시중교재(PUBLIC) / 교과서(SCHOOL) / 내교재(CUSTOM) → CUSTOM_WORKBOOK_WRITE 로 통합
 * - 시그니처(SIGNATURE / CUSTOM_SIGNATURE) → SIGNATURE_WORKBOOK_WRITE
 * - 학원그룹교재(ACADEMY_GROUP) → null (모두 지원, write 차단 키 없음 — resolveViewerMode 가 'student' fallback)
 */
export const resolveWorkbookWriteKey = (
  workbookType: Workbook['type'],
): StudentFeatureKey | null => {
  switch (workbookType) {
    case 'PUBLIC':
    case 'SCHOOL':
    case 'CUSTOM':
      return 'CUSTOM_WORKBOOK_WRITE'
    case 'SIGNATURE':
    case 'CUSTOM_SIGNATURE':
      return 'SIGNATURE_WORKBOOK_WRITE'
    case 'ACADEMY_GROUP':
    case 'SCHOOL_TEST':
      return null
  }
}

/**
 * 교재(WORKBOOK) type 별 풀이동영상 restriction 키.
 * - 시중교재(PUBLIC) / 교과서(SCHOOL) / 내교재(CUSTOM) → CUSTOM_WORKBOOK_PROBLEM_SOLVED_VIDEO 로 통합
 * - 시그니처(SIGNATURE/CUSTOM_SIGNATURE) → SIGNATURE_WORKBOOK_PROBLEM_SOLVED_VIDEO
 * - 학원그룹교재(ACADEMY_GROUP) → null (모두 지원 케이스, 차단 키 자체 없음 — 항상 허용)
 */
export const resolveWorkbookProblemSolvedVideoKey = (
  workbookType: Workbook['type'],
): StudentFeatureKey | null => {
  switch (workbookType) {
    case 'PUBLIC':
    case 'SCHOOL':
    case 'CUSTOM':
      return 'CUSTOM_WORKBOOK_PROBLEM_SOLVED_VIDEO'
    case 'SIGNATURE':
    case 'CUSTOM_SIGNATURE':
      return 'SIGNATURE_WORKBOOK_PROBLEM_SOLVED_VIDEO'
    case 'ACADEMY_GROUP':
    case 'SCHOOL_TEST':
      return null
  }
}

/**
 * 교재(WORKBOOK) type 별 콘텐츠 자체 차단 키.
 * - 시중교재(PUBLIC) / 교과서(SCHOOL) / 내교재(CUSTOM) → null (콘텐츠 차단 키 없음 = 항상 허용)
 * - 시그니처(SIGNATURE / CUSTOM_SIGNATURE) → SIGNATURE_WORKBOOK
 * - 학원그룹교재(ACADEMY_GROUP) → ACADEMY_GROUP_WORKBOOK
 */
export const resolveWorkbookContentKey = (
  workbookType: Workbook['type'],
): StudentFeatureKey | null => {
  switch (workbookType) {
    case 'SIGNATURE':
    case 'CUSTOM_SIGNATURE':
      return 'SIGNATURE_WORKBOOK'
    case 'ACADEMY_GROUP':
      return 'ACADEMY_GROUP_WORKBOOK'
    case 'PUBLIC':
    case 'SCHOOL':
    case 'CUSTOM':
    case 'SCHOOL_TEST':
      return null
  }
}

/**
 * 주어진 workbook type 의 콘텐츠가 허용되는지 여부.
 * resolveWorkbookContentKey 가 null 이면 가드 키 없는 type 이라 항상 허용.
 */
export const isWorkbookTypeAllowed = (
  permission: UserPermission,
  workbookType: Workbook['type'],
): boolean => {
  const key = resolveWorkbookContentKey(workbookType)
  if (key === null) return true
  return permission.byFeature.isAllowed(key)
}

/**
 * 교재(WORKBOOK) 라우팅 분기 SSOT — list/홈 진입 시 viewer/detail 결정.
 *
 * - 커스텀시그니처(CUSTOM_SIGNATURE): SIGNATURE_WORKBOOK_PDF 차단 시 detail fallback, 아니면 viewer
 * - 그 외: detail
 */
export const resolveWorkbookViewRoute = (
  workbookType: Workbook['type'],
  permission: UserPermission,
): 'detail' | 'viewer' => {
  switch (workbookType) {
    case 'CUSTOM_SIGNATURE':
      return permission.byFeature.isAllowed('SIGNATURE_WORKBOOK_PDF') ? 'viewer' : 'detail'
    default:
      return 'detail'
  }
}
